import _extends from "@babel/runtime/helpers/extends";
import { useComboBox } from "@react-aria/combobox";
import { useFilter } from "@react-aria/i18n";
import { useComboBoxState } from "@react-stately/combobox";
import React, { useEffect, useRef } from "react";
import { extractStylingProps } from "../../shared";
import { Input } from "../Form";
import { Label } from "../Typography";
import { FormField } from "../internal/FormField";
import { ComboBoxListBox } from "./ComboBoxListBox";
import { ComboBoxPopover } from "./ComboBoxPopover";
export function ComboBoxRoot(_ref) {
  var _inputRef$current;
  let {
    className,
    disabled,
    error,
    invalid = Boolean(error),
    layout,
    required,
    caption = error,
    hint,
    toolbar,
    captionAreaProps,
    containerProps,
    contentAreaProps,
    labelAreaProps,
    hintAreaProps,
    inputProps: customInputProps,
    toolbarAreaProps,
    ...stylingAndAriaProps
  } = _ref;
  const [stylingProps, ariaProps] = extractStylingProps({
    ...stylingAndAriaProps,
    isDisabled: disabled,
    isInvalid: invalid,
    isRequired: required
  });
  const {
    contains
  } = useFilter({
    sensitivity: "base"
  });
  const state = useComboBoxState({
    defaultFilter: contains,
    ...ariaProps
  });
  useAsyncItemsWorkaround(ariaProps, state);
  const inputRef = useRef(null);
  const listBoxRef = useRef(null);
  const popoverRef = useRef(null);
  const {
    labelProps,
    listBoxProps,
    inputProps
  } = useComboBox({
    ...ariaProps,
    inputRef,
    listBoxRef,
    popoverRef
  }, state);
  const inputWidth = (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.offsetWidth;
  return /*#__PURE__*/React.createElement(FormField.Root, _extends({}, containerProps, stylingProps, {
    className: className,
    layout: layout
  }), /*#__PURE__*/React.createElement(FormField.LabelArea, labelAreaProps, /*#__PURE__*/React.createElement(Label, _extends({}, labelProps, {
    variant: invalid ? "danger" : undefined
  }), ariaProps.label)), hint && /*#__PURE__*/React.createElement(FormField.HintArea, hintAreaProps, hint), toolbar && /*#__PURE__*/React.createElement(FormField.ToolbarArea, toolbarAreaProps, toolbar), /*#__PURE__*/React.createElement(FormField.ContentArea, contentAreaProps, /*#__PURE__*/React.createElement(Input, _extends({}, customInputProps, inputProps, {
    disabled: disabled,
    invalid: invalid,
    ref: inputRef
  })), state.isOpen && /*#__PURE__*/React.createElement(ComboBoxPopover, {
    placement: "bottom start",
    popoverRef: popoverRef,
    shouldFlip: true,
    state: state,
    triggerRef: inputRef
  }, /*#__PURE__*/React.createElement(ComboBoxListBox, _extends({}, listBoxProps, {
    inputWidth: inputWidth,
    listBoxRef: listBoxRef,
    state: state
  })))), (error || caption) && /*#__PURE__*/React.createElement(FormField.CaptionArea, _extends({
    variant: error ? "danger" : undefined
  }, captionAreaProps), error || caption));
}

/**
 * A custom hook that provides a workaround for handling asynchronous items in a
 * ComboBox component. It ensures that the ComboBox list is opened to show the
 * loaded items when certain conditions are met.
 *
 * @remarks
 * This hook addresses the issue where the ComboBox list does not open
 * automatically when items are loaded asynchronously. It manually opens the
 * list if:
 *
 * - There is at least one item.
 * - The input is focused.
 * - The selection did not just change (the list closes then and should not
 *   reopen).
 * - The input value is different from the selected item's text value (also
 *   prevents incorrect reopening).
 *
 * Related issues on GitHub:
 *
 * - [#2464 Controllable open state for
 *   combobox](https://github.com/adobe/react-spectrum/issues/2464)
 * - [#5133 Popover and ListBox inside ComboBox behave
 *   incorrectly](https://github.com/adobe/react-spectrum/issues/5133)
 * - [#3392 useComboBox Async Loading example unexpected
 *   behavior](https://github.com/adobe/react-spectrum/issues/3392)
 * - [#5234 Async Combobox not rendering initial
 *   result](https://github.com/adobe/react-spectrum/issues/5234)
 *
 * @template T - The type of the items in the ComboBox.
 * @param {AriaComboBoxProps<T>} props - The properties of the ComboBox
 *   component.
 * @param {ComboBoxState<T>} state - The state of the ComboBox component.
 */
function useAsyncItemsWorkaround(props, state) {
  const prevSelectedTextValue = useRef();
  useEffect(() => {
    var _state$selectedItem, _state$selectedItem2, _state$selectedItem3;
    if (
    // If there is at least one item,
    props.items && [...props.items].length > 0 &&
    // the input is focused,
    state.isFocused &&
    // the selection did not just change (the list closes then and should not reopen),
    prevSelectedTextValue.current === ((_state$selectedItem = state.selectedItem) === null || _state$selectedItem === void 0 ? void 0 : _state$selectedItem.textValue) &&
    // and the input value is different from the selected item's text value (also prevents incorrect reopening),
    state.inputValue !== ((_state$selectedItem2 = state.selectedItem) === null || _state$selectedItem2 === void 0 ? void 0 : _state$selectedItem2.textValue)) {
      // then manually open the list to show the loaded items.
      state.open();
    }
    if ((_state$selectedItem3 = state.selectedItem) !== null && _state$selectedItem3 !== void 0 && _state$selectedItem3.textValue) {
      var _state$selectedItem4;
      // Remember the selected item's text value. The condition above prevents
      // the remembered value from being incorrectly reset. While testing,
      // I found that the selected item's text value is sometimes set to undefined,
      // even though the selected item is still the same.
      prevSelectedTextValue.current = (_state$selectedItem4 = state.selectedItem) === null || _state$selectedItem4 === void 0 ? void 0 : _state$selectedItem4.textValue;
    }
    if (state.inputValue === "") {
      // Empty input value resets selection, so we reset the remembered value.
      prevSelectedTextValue.current = undefined;
    }
  }, [props.items, state.inputValue, state.isFocused, state.selectedItem]);
}
//# sourceMappingURL=ComboBoxRoot.js.map